import type { OrchestrationQueuedTurn } from "@t3tools/contracts";
import { CheckIcon, CornerDownLeftIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react";

import { i18n } from "../../i18n";
import { Button } from "../ui/button";

export function QueuedTurnList({
  turns,
  editingMessageId,
  canSteer,
  onEdit,
  onDelete,
  onSteer,
  onSaveEdit,
  onCancelEdit,
}: {
  readonly turns: ReadonlyArray<OrchestrationQueuedTurn>;
  readonly editingMessageId?: string | null;
  readonly canSteer?: boolean;
  readonly onEdit?: (turn: OrchestrationQueuedTurn) => void;
  readonly onDelete?: (turn: OrchestrationQueuedTurn) => void;
  readonly onSteer?: (turn: OrchestrationQueuedTurn) => void;
  readonly onSaveEdit?: () => void;
  readonly onCancelEdit?: () => void;
}) {
  if (turns.length === 0) return null;

  return (
    <section
      aria-label={i18n.t("composer.queueLabel")}
      className="mx-auto mb-2 max-h-[30dvh] w-full max-w-3xl overflow-y-auto rounded-xl border border-border/60 bg-background/88 px-3 py-2 shadow-sm backdrop-blur-xl"
    >
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">
        {i18n.t("composer.queueCount", { count: turns.length })}
      </div>
      <ol className="space-y-1">
        {turns.map((turn) => {
          const isEditing = editingMessageId === turn.messageId;
          const canOperate = turn.status === "queued";
          return (
            <li
              key={turn.messageId}
              className="group flex min-w-0 items-center gap-2 rounded-lg bg-muted/45 px-2.5 py-1.5 text-sm"
            >
              <span className="min-w-0 flex-1 truncate">
                {turn.text.trim() || i18n.t("composer.queueAttachmentOnly")}
              </span>
              {turn.status === "dispatching" ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {i18n.t("composer.queueDispatching")}
                </span>
              ) : null}
              {turn.status === "steering" ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {i18n.t("composer.queueSteering")}
                </span>
              ) : null}
              {canOperate ? (
                <div className="flex shrink-0 items-center gap-0.5">
                  {isEditing ? (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={i18n.t("composer.queueSaveEdit")}
                        title={i18n.t("composer.queueSaveEdit")}
                        onClick={onSaveEdit}
                      >
                        <CheckIcon className="size-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={i18n.t("composer.queueCancelEdit")}
                        title={i18n.t("composer.queueCancelEdit")}
                        onClick={onCancelEdit}
                      >
                        <XIcon className="size-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={i18n.t("composer.queueEdit")}
                        title={i18n.t("composer.queueEdit")}
                        onClick={() => onEdit?.(turn)}
                      >
                        <PencilIcon className="size-3.5" />
                      </Button>
                      {canSteer ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-xs"
                          aria-label={i18n.t("composer.queueSteer")}
                          title={i18n.t("composer.queueSteer")}
                          onClick={() => onSteer?.(turn)}
                        >
                          <CornerDownLeftIcon className="size-3.5" />
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={i18n.t("composer.queueDelete")}
                        title={i18n.t("composer.queueDelete")}
                        onClick={() => onDelete?.(turn)}
                      >
                        <Trash2Icon className="size-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
